import { appFetch } from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;

    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }

    return { ok, data: payload as T, error: (payload as any)?.error };
}

export type V2TemplateCreateBody = {
    channel: "whatsapp";
    provider: "twilio";
    title: string;
    language: string;
    category: "MARKETING" | "UTILITY";
    content_types: string[];
    types_config: any;
    variables_schema: string[];
    metadata?: any;
};

export const CreateV2Template = async (domain: string, body: V2TemplateCreateBody) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });

    return unwrap<{ id: number }>(res);
};

export const GetV2Template = async (domain: string, templateId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ template: any }>(res);
};

export const UpdateV2Template = async (domain: string, templateId: number, body: V2TemplateCreateBody) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });
    return unwrap<{ id: number }>(res);
};

export type V2TemplateListItemDto = {
    id: number;
    title: string;
    channel: string;
    status: string;
    category: string | null;
    provider_template_name?: string;
    variables_schema_json?: any;
    content_types_json?: any;
    types_config_json?: any;
    metadata_json?: any;
    status_details?: any;
    provider_content_sid?: string | null;
    stats?: {
        sent_total?: number;
        clicks_total?: number;
        sent_month?: number;
        clicks_month?: number;
        sent_week?: number;
        clicks_week?: number;
        automations_total?: number;
    };
};

export const ListV2Templates = async (domain: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/ai/automation/context/templates`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ templates: V2TemplateListItemDto[] }>(res);
};

export const SubmitV2TemplateToTwilio = async (domain: string, templateId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}/submit-to-twilio`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {},
    });

    return unwrap<any>(res);
};

export const PreviewV2Template = async (domain: string, templateId: number, to: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}/preview`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: { to },
    });

    return unwrap<any>(res);
};

export const RetryRejectedV2TemplateAsDraft = async (domain: string, templateId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}/retry-as-draft`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {},
    });

    return unwrap<any>(res);
};

export const DeleteV2Template = async (domain: string, templateId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/templates/${templateId}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        domain,
        params: {},
    });

    return unwrap<any>(res);
};
